import { useState } from "react";

import Head from "next/head";
import NextLink from "next/link";
import { useRouter } from "next/router";

import { resetUserPassword } from "../../api/user";

import {
    Box,
    Button,
    Card,
    Container,
    FormHelperText,
    Typography,
    TextField,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import * as Yup from "yup";
import { useFormik } from "formik";
import { useSettings } from "../../hooks/use-settings";
import { Logo } from "../../components/logo";

const PasswordRecovery = () => {
    const [isPostSubmit, setIsPostSubmit] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const router = useRouter();
    const { settings } = useSettings();

    const formik = useFormik({
        initialValues: {
            email: "",
            submit: null,
        },
        validationSchema: Yup.object({
            email: Yup.string()
                .email("Must be a valid email")
                .max(255)
                .required("Email is required"),
        }),
        onSubmit: async (values, helpers) => {
            setIsLoading(true);

            try {
                const res = await resetUserPassword(values.email);
                setIsLoading(false);

                helpers.setStatus({ success: true });
                setIsPostSubmit(true);
            } catch (error) {
                isLoading && setIsLoading(false);
                helpers.setStatus({ success: false });
                helpers.setErrors({ submit: error.message });
                helpers.setSubmitting(false);
            }
        },
    });

    return (
        <>
            <Head>
                <title>Password Recovery | TCA Developments</title>
            </Head>
            <Box
                component="main"
                sx={{
                    backgroundColor: "background.default",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: "100vh",
                }}
            >
                <Container
                    maxWidth="sm"
                    sx={{
                        py: {
                            xs: "60px",
                            md: "120px",
                        },
                    }}
                >
                    <Card elevation={16} sx={{ p: 4 }}>
                        <Box
                            sx={{
                                alignItems: "center",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                            }}
                        >
                            <NextLink href="/dashboard" passHref>
                                <a>
                                    <Logo
                                        showdark={String(
                                            settings.theme === "light"
                                        )}
                                        width="120px"
                                        height="90px"
                                    />
                                </a>
                            </NextLink>
                            {isPostSubmit ? (
                                <>
                                    <Typography
                                        color="textSecondary"
                                        sx={{ my: 3 }}
                                        variant="body1"
                                    >
                                        Success! A password reset has been
                                        initiated. Please check your email for
                                        next steps.
                                    </Typography>
                                    <Button
                                        fullWidth
                                        size="large"
                                        type="button"
                                        onClick={() =>
                                            router.push("/authentication/login")
                                        }
                                        variant="contained"
                                    >
                                        Back to login
                                    </Button>
                                </>
                            ) : (
                                <form noValidate onSubmit={formik.handleSubmit}>
                                    <Typography
                                        color="textPrimary"
                                        sx={{ mt: 2 }}
                                        variant="body2"
                                    >
                                        Forgotten your password? Please enter
                                        your email below and we will send
                                        instructions for setting a new one.
                                    </Typography>

                                    <TextField
                                        autoFocus
                                        error={Boolean(
                                            formik.touched.email &&
                                                formik.errors.email
                                        )}
                                        fullWidth
                                        helperText={
                                            formik.touched.email &&
                                            formik.errors.email
                                        }
                                        label="Email Address"
                                        margin="normal"
                                        name="email"
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        type="email"
                                        value={formik.values.email}
                                    />
                                    {formik.errors.submit && (
                                        <Box sx={{ mt: 3 }}>
                                            <FormHelperText error>
                                                {formik.errors.submit}
                                            </FormHelperText>
                                        </Box>
                                    )}
                                    <Box sx={{ mt: 2 }}>
                                        <LoadingButton
                                            loading={isLoading}
                                            disabled={formik.isSubmitting}
                                            fullWidth
                                            size="large"
                                            type="submit"
                                            variant="contained"
                                        >
                                            Reset Password
                                        </LoadingButton>
                                    </Box>
                                </form>
                            )}
                        </Box>
                    </Card>
                </Container>
            </Box>
        </>
    );
};

PasswordRecovery.public = true;
export default PasswordRecovery;
